/**
 * ✅ INTELLIGENCE STUDIO API
 *
 * Frontend API wrapper for Intelligence Studio (New Engine).
 * Uses the existing apiClient for auth and error handling.
 *
 * @module intelligenceStudioApi
 */

import rawApiClient from '../services/apiClient';
import {
  createTraceSpan,
  endTraceSpan,
  getTraceQuery,
  recordRemoteSpan,
} from '../utils/traceContext';
import { authService } from '../services/auth/AuthService';
import { getApiBaseUrl } from '../config/runtimeUrls';
import { getAuthToken } from '../utils/auth';

const BASE_PATH = '/api/intelligence-studio';

const resolvePayload = response => response?.data ?? response;
const normalizeResponse = response => {
  const payload = resolvePayload(response);
  if (response?.success === false) {
    return response;
  }
  return payload;
};

const apiClient = {
  defaults: rawApiClient.defaults,
  get: async (...args) => normalizeResponse(await rawApiClient.get(...args)),
  post: async (...args) => normalizeResponse(await rawApiClient.post(...args)),
  put: async (...args) => normalizeResponse(await rawApiClient.put(...args)),
  patch: async (...args) => normalizeResponse(await rawApiClient.patch(...args)),
  delete: async (...args) => normalizeResponse(await rawApiClient.delete(...args)),
  uploadFile: async (...args) => normalizeResponse(await rawApiClient.uploadFile(...args)),
};

// ✅ Treat abort/cancel as expected control-flow (unmount, route change, retry swap)
const isCanceledRequest = error =>
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED' ||
  String(error?.message || '').toLowerCase().includes('canceled');

const bytesToHex = buffer =>
  Array.from(new Uint8Array(buffer))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

const computeFileSha256 = async file => {
  if (!globalThis.crypto?.subtle || !file?.arrayBuffer) return null;
  const buffer = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return bytesToHex(digest);
};

const resolveSseToken = tokenHint => {
  let token = '';
  try {
    token = authService?.getAccessToken?.() || '';
  } catch (_) {}
  if (!token) {
    token = getAuthToken() || tokenHint || '';
  }
  token = String(token || '').trim();
  const lower = token.toLowerCase();
  if (!token || lower === 'null' || lower === 'undefined' || token.split('.').length < 3) {
    return '';
  }
  return token;
};

const DEFAULT_SSE_API_BASE_URL = 'https://api.bassitai.com/api';

const resolveAbsoluteSseApiBaseUrl = () => {
  let baseUrl = String(apiClient.defaults?.baseURL || getApiBaseUrl() || '').trim();
  if (!baseUrl || !/^https?:\/\//i.test(baseUrl)) {
    console.error('[SSE] API base URL must be absolute for EventSource. Got:', baseUrl);
    baseUrl = DEFAULT_SSE_API_BASE_URL;
  }
  return baseUrl.replace(/\/+$/, '');
};

const putPresignedFile = (url, file, headers = {}, onProgress = null) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    Object.entries(headers || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        xhr.setRequestHeader(key, String(value));
      }
    });
    xhr.upload.onprogress = event => {
      if (!onProgress || !event.lengthComputable) return;
      const percent = Math.round((event.loaded * 100) / event.total);
      onProgress({ stage: 'uploading', percent, message: `Uploading ${percent}%...` });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`PRESIGNED_UPLOAD_FAILED_${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('PRESIGNED_UPLOAD_FAILED'));
    xhr.send(file);
  });

// ==========================================
// STATUS
// ==========================================

/**
 * Get engine status
 */
export const getStatus = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/status`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Status failed:', error);
    throw error;
  }
};

/**
 * Get quota/usage status (for utilization indicator + user-friendly limit messaging)
 */
export const getQuotaStatus = async () => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/quota`);
    return response;
  } catch (error) {
    console.warn('⚠️ [IntelligenceStudio API] Quota status failed:', error?.message || error);
    // Non-critical: UI can still function without the indicator
    return { success: false, data: null };
  }
};

// ==========================================
// NOTEBOOK CRUD
// ==========================================

/**
 * Create a new notebook
 * @param {string} title - Notebook title
 */
export const createNotebook = async title => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks`, { title });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Create failed:', error);
    throw error;
  }
};

/**
 * List all notebooks
 */
export const listNotebooks = async (options = {}) => {
  try {
    const requestedLimit = Number(options?.limit);
    const requestedOffset = Number(options?.offset);
    const limit = Number.isFinite(requestedLimit) && requestedLimit > 0 ? requestedLimit : 50;
    const offset = Number.isFinite(requestedOffset) && requestedOffset >= 0 ? requestedOffset : 0;
    const includeState = options?.includeState === true ? 'true' : 'false';
    const includeFiles = options?.includeFiles === true ? 'true' : 'false';
    const response = await apiClient.get(`${BASE_PATH}/notebooks`, {
      params: {
        limit,
        offset,
        includeState,
        includeFiles,
      },
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] List failed:', error);
    throw error;
  }
};

/**
 * Get a single notebook
 * @param {string} id - Notebook ID
 * ✅ FIX: apiClient.get() returns response.data directly
 */
export const getNotebook = async id => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${id}`);
    const payload = response?.data ?? response;
    // ✅ DEBUG: Log response structure
    console.log('📓 [API] getNotebook response:', {
      success: response?.success,
      hasData: !!payload,
      fileCount: payload?.files?.length || 0,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get failed:', error);
    throw error;
  }
};

/**
 * Get canonical vault state for a notebook
 * @param {string} id - Notebook ID
 * @param {Object} options - { includeFiles, includeQdrant }
 */
export const getNotebookState = async (id, options = {}) => {
  try {
    const params = {};
    if (options.includeFiles === false) params.includeFiles = 'false';
    if (options.includeQdrant === false) params.includeQdrant = 'false';
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${id}/state`, { params });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] State failed:', error);
    throw error;
  }
};

/**
 * Update a notebook
 * @param {string} id - Notebook ID
 * @param {string} title - New title
 */
export const updateNotebook = async (id, title) => {
  try {
    const response = await apiClient.put(`${BASE_PATH}/notebooks/${id}`, { title });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Update failed:', error);
    throw error;
  }
};

/**
 * Delete a notebook
 * @param {string} id - Notebook ID
 */
export const deleteNotebook = async id => {
  try {
    const response = await apiClient.delete(`${BASE_PATH}/notebooks/${id}`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Delete failed:', error);
    throw error;
  }
};

// ==========================================
// FILE MANAGEMENT
// ==========================================

/**
 * ✅ SMART PRE-CHECK: Check if file should be uploaded
 * Call this BEFORE uploadFile to get intelligent recommendations
 *
 * @param {string} notebookId - Notebook ID
 * @param {File} file - File to check
 * @returns {Object} { shouldUpload, status, reason, action, existingFileId, cachedQuality }
 */
export const checkFileBeforeUpload = async (notebookId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/files/check`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    console.log(`🔍 [SmartUpload] Pre-check result for "${file.name}":`, response);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Pre-check failed:', error);
    // On error, allow upload to proceed (fail-open for UX)
    return {
      success: true,
      shouldUpload: true,
      status: 'CHECK_FAILED',
      reason: 'Could not verify - proceeding with upload',
      action: 'UPLOAD_FULL',
    };
  }
};

/**
 * ✅ SMART UPLOAD: Intelligent upload with pre-check
 * Automatically checks deduplication and caching before upload
 *
 * @param {string} notebookId - Notebook ID
 * @param {File} file - File to upload
 * @param {Function} onProgress - Progress callback
 * @param {Object} options - { skipCheck: boolean }
 */
export const smartUpload = async (notebookId, file, onProgress = null, options = {}) => {
  // Step 1: Pre-check (unless skipped)
  if (!options.skipCheck) {
    const check = await checkFileBeforeUpload(notebookId, file);

    if (!check.shouldUpload) {
      // Duplicate found - return the check result
      return {
        success: false,
        isDuplicate: true,
        ...check,
      };
    }

    // Notify frontend about expected speed
    if (onProgress && check.action === 'UPLOAD_FAST') {
      onProgress({ stage: 'cached', percent: 50, message: '⚡ Using cached data - instant!' });
    }
  }

  // Step 2: Actual upload
  return uploadFile(notebookId, file, onProgress);
};

/**
 * Upload and process a file
 * @param {string} notebookId - Notebook ID
 * @param {File} file - File to upload
 * @param {Function} onProgress - Progress callback
 */
export const uploadFile = async (notebookId, file, onProgress = null) => {
  try {
    const mimeType = file?.type || 'application/octet-stream';
    const session = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/uploads/session`, {
      fileName: file?.name || 'upload.bin',
      mimeType,
      size: file?.size || 0,
    });

    const sessionData = session?.data ?? session;
    if (session?.success === false) {
      const message =
        session?.error?.message ||
        session?.message ||
        session?.error ||
        'UPLOAD_SESSION_FAILED';
      throw new Error(message);
    }
    if (!sessionData?.stagingUri || !sessionData?.urls?.length) {
      throw new Error('UPLOAD_SESSION_FAILED');
    }

    if (onProgress) {
      onProgress({ stage: 'staging', percent: 5, message: 'Preparing upload...' });
    }

    await putPresignedFile(sessionData.urls[0], file, sessionData.headers, onProgress);

    const sha256 = await computeFileSha256(file);
    if (onProgress) {
      onProgress({ stage: 'finalizing', percent: 95, message: 'Finalizing upload...' });
    }

    const finalize = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/uploads/finalize`,
      {
        sha256,
        size: file?.size || 0,
        mime: mimeType,
        stagingUri: sessionData.stagingUri,
        notebookId,
        fileName: file?.name || 'upload.bin',
      }
    );

    if (onProgress) {
      onProgress({ stage: 'finalized', percent: 100, message: 'Upload queued' });
    }

    return finalize;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Upload failed:', error);
    throw error;
  }
};

// ==========================================
// PHASE 2: SEMANTIC MEMORY
// ==========================================

export const getMemorySettings = async () => {
  const response = await apiClient.get(`${BASE_PATH}/memory/settings`);
  return response;
};

export const updateMemorySettings = async (patch = {}) => {
  const response = await apiClient.put(`${BASE_PATH}/memory/settings`, patch);
  return response;
};

export const queryMemory = async (text, topK = 8) => {
  const response = await apiClient.get(`${BASE_PATH}/memory/query`, {
    params: { text, topK },
  });
  return response;
};

export const clearMemory = async () => {
  const response = await apiClient.post(`${BASE_PATH}/memory/clear`, {});
  return response;
};

/**
 * Get processing progress (uses SSE)
 * @param {string} notebookId - Notebook ID
 * @param {Function} onProgress - Progress callback
 */
export const subscribeProgress = (notebookId, onProgress) => {
  const baseUrl = resolveAbsoluteSseApiBaseUrl().replace(/\/api\/?$/, '');
  const traceSpan = createTraceSpan({
    apiName: 'sse.progress',
    pipelineStage: 'sse.progress',
    notebookId,
    tip: 'EventSource',
  });
  const traceQuery = getTraceQuery(traceSpan);
  const qs = new URLSearchParams({
    traceId: traceQuery.traceId,
    spanId: traceQuery.spanId,
  });
  const eventSource = new EventSource(
    `${baseUrl}${BASE_PATH}/notebooks/${notebookId}/progress?${qs.toString()}`
  );

  eventSource.onmessage = event => {
    try {
      const data = JSON.parse(event.data);
      try {
        if (data?.trace?.traceId && data?.trace?.spanId) {
          recordRemoteSpan(data.trace);
        }
      } catch (_) {}
      onProgress(data);

      if (data.status === 'ready' || data.status === 'error') {
        eventSource.close();
        try {
          endTraceSpan(traceSpan.spanId, { status: data.status === 'error' ? 'error' : 'ok' });
        } catch (_) {}
      }
    } catch (e) {
      console.error('Failed to parse progress:', e);
    }
  };

  eventSource.onerror = () => {
    try {
      endTraceSpan(traceSpan.spanId, { status: 'error', error: 'SSE progress error' });
    } catch (_) {}
    eventSource.close();
  };

  return () => eventSource.close();
};

/**
 * Delete a file from notebook
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 */
export const deleteFile = async (notebookId, fileId) => {
  try {
    const response = await apiClient.delete(`${BASE_PATH}/notebooks/${notebookId}/files/${fileId}`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Delete file failed:', error);
    throw error;
  }
};

/**
 * ✅ ENTERPRISE: Force re-process a file with poor OCR quality
 * Bypasses cache and re-runs OCR extraction
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 * @param {string} reason - Optional reason for re-processing
 */
export const reprocessFile = async (notebookId, fileId, options = 'Poor OCR quality') => {
  try {
    const payload =
      typeof options === 'string'
        ? { reason: options }
        : {
            reason: options?.reason || 'Poor OCR quality',
            forceEngine: options?.forceEngine,
            tryCascade: options?.tryCascade,
            qualityThreshold: options?.qualityThreshold,
          };

    // Remove undefined keys (keeps request clean)
    Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

    console.log(`🔄 [IntelligenceStudio API] Re-processing file ${fileId} - Reason: ${payload.reason}`);
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/files/${fileId}/reprocess`,
      payload
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Reprocess file failed:', error);
    throw error;
  }
};

/**
 * ✅ ENTERPRISE: Re-index file (FAST PATH)
 * Re-stamp from Global FULL_CACHE and confirm vectors exist (no OCR cascade).
 */
export const reindexFile = async (
  notebookId,
  fileId,
  options = 'Re-index: missing search index'
) => {
  try {
    const payload = typeof options === 'string' ? { reason: options } : { ...(options || {}) };
    console.log(
      `🧱 [IntelligenceStudio API] Re-indexing file ${fileId} - Reason: ${payload.reason || 'Re-index'}`
    );
    return await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/files/${fileId}/reindex`,
      payload
    );
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Reindex file failed:', error);
    throw error;
  }
};

/**
 * Rename a file in notebook
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 * @param {string} newName - New file name
 */
export const renameFile = async (notebookId, fileId, newName) => {
  try {
    const response = await apiClient.patch(`${BASE_PATH}/notebooks/${notebookId}/files/${fileId}`, {
      name: newName,
      originalName: newName,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Rename file failed:', error);
    throw error;
  }
};

// ==========================================
// CHAT
// ==========================================

/**
 * Ask a question
 * @param {string} notebookId - Notebook ID
 * @param {string} question - User question
 * @param {Object} options - Options (threadId, mode)
 */
export const askQuestion = async (notebookId, question, options = {}) => {
  try {
    const { threadId, thread_id, mode = 'fast' } = options;
    const resolvedThreadId = threadId || thread_id;
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/ask`, {
      question,
      threadId: resolvedThreadId, // ✅ Pass threadId for atomic message saving
      mode, // ✅ ULTRA: 'fast' or 'thinking' mode
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Ask failed:', error);
    throw error;
  }
};

/**
 * Get chat history
 * @param {string} notebookId - Notebook ID
 */
export const getChatHistory = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/chat`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get history failed:', error);
    throw error;
  }
};

/**
 * Clear chat history
 * @param {string} notebookId - Notebook ID
 */
export const clearChatHistory = async notebookId => {
  try {
    const response = await apiClient.delete(`${BASE_PATH}/notebooks/${notebookId}/chat`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Clear history failed:', error);
    throw error;
  }
};

// ==========================================
// NOTEBOOK BRAIN APIs
// ==========================================

/**
 * Advanced AI query using NotebookBrain
 * @param {string} notebookId - Notebook ID
 * @param {string} question - User question
 */
export const queryBrain = async (notebookId, question) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/query`, {
      question,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Brain query failed:', error);
    throw error;
  }
};

/**
 * Compare documents in notebook
 * @param {string} notebookId - Notebook ID
 * @param {string[]} docIds - Document IDs to compare
 * @param {string} aspect - Optional aspect to focus on
 */
export const compareDocuments = async (notebookId, docIds, aspect = null) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/compare`, {
      docIds,
      aspect,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Compare failed:', error);
    throw error;
  }
};

/**
 * Get graph data for visualization
 * @param {string} notebookId - Notebook ID
 */
export const getGraph = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/graph`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get graph failed:', error);
    throw error;
  }
};

/**
 * Find conflicts across documents
 * @param {string} notebookId - Notebook ID
 */
export const findConflicts = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/conflicts`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Find conflicts failed:', error);
    throw error;
  }
};

/**
 * Deterministic count across canonical extracted text (no LLM).
 * POST /api/intelligence-studio/notebooks/:id/deterministic/count
 *
 * @param {string} notebookId - Notebook ID
 * @param {Object} payload
 * @param {string} payload.term
 * @param {string[]} [payload.fileIds]
 * @param {'exact'|'whole_word'} [payload.mode]
 * @param {'sensitive'|'insensitive'} [payload.case] - UI-friendly alias
 * @param {'sensitive'|'insensitive'} [payload.caseMode] - API canonical field
 * @param {boolean} [payload.regex]
 * @param {string} [payload.flags]
 */
export const deterministicCount = async (notebookId, payload = {}) => {
  try {
    const { term, fileIds, mode, case: uiCaseMode, caseMode, regex, flags } = payload || {};

    const body = {
      term,
      fileIds,
      mode,
      caseMode: caseMode || uiCaseMode,
      regex,
      flags,
    };

    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/deterministic/count`,
      body
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Deterministic count failed:', error);
    throw error;
  }
};

/**
 * Build timeline from documents
 * @param {string} notebookId - Notebook ID
 */
export const buildTimeline = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/timeline`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Build timeline failed:', error);
    throw error;
  }
};

// ==========================================
// INSIGHT MEMORY + VAULT IDENTITY APIs
// ==========================================

/**
 * Get Insight Memory (persisted proactive pounces + user actions)
 * @param {string} notebookId - Notebook ID
 */
export const getInsightMemory = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/insight-memory`);
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [IntelligenceStudio API] Get insight memory failed:', error);
    throw error;
  }
};

/**
 * Upsert Insight Memory entry
 * @param {string} notebookId - Notebook ID
 * @param {Object} entry - Memory entry payload
 */
export const upsertInsightMemory = async (notebookId, entry) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/insight-memory`,
      entry
    );
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [IntelligenceStudio API] Upsert insight memory failed:', error);
    throw error;
  }
};

/**
 * Record Insight Memory event (clicked/asked/dismissed/etc.)
 * @param {string} notebookId - Notebook ID
 * @param {string} memoryId - Memory entry id (or memoryKey)
 * @param {Object} event - Event payload
 */
export const recordInsightMemoryEvent = async (notebookId, memoryId, event) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/insight-memory/${memoryId}/event`,
      event
    );
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [IntelligenceStudio API] Record insight memory event failed:', error);
    throw error;
  }
};

/**
 * Force-generate (or regenerate) vault narrative identity
 * @param {string} notebookId - Notebook ID
 */
export const generateVaultNarrative = async notebookId => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/vault-narrative`,
      {}
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Generate vault narrative failed:', error);
    throw error;
  }
};

/**
 * Ask graph using natural language
 * @param {string} notebookId - Notebook ID
 * @param {string} question - Natural language question
 */
export const askGraph = async (notebookId, question) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/ask-graph`, {
      question,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Ask graph failed:', error);
    throw error;
  }
};

/**
 * Hybrid search (vector + graph)
 * @param {string} notebookId - Notebook ID
 * @param {string} query - Search query
 * @param {Object} options - Search options
 */
export const hybridSearch = async (notebookId, query, options = {}) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/search`, {
      query,
      options,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Hybrid search failed:', error);
    throw error;
  }
};

// ==========================================
// QUERY ENGINE APIs (Advanced Harvey-style)
// ==========================================

/**
 * Run unified query through Query Engine
 * Uses hybrid search (vector + graph) with AI reasoning
 * @param {string} notebookId - Notebook ID
 * @param {string} question - User question
 * @param {string[]} fileIds - Optional specific file IDs to search
 */
export const runQuery = async (notebookId, question, fileIds = null) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/query`, {
      question,
      fileIds,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Query failed:', error);
    throw error;
  }
};

/**
 * Get knowledge graph for a notebook
 * @param {string} notebookId - Notebook ID
 */
export const getKnowledgeGraph = async notebookId => {
  try {
    // ✅ FIX: Correct API path
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/graph`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get graph failed:', error);
    throw error;
  }
};

/**
 * Rebuild knowledge graph for existing documents
 * @param {string} notebookId - Notebook ID
 */
export const rebuildGraph = async notebookId => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/rebuild-graph`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Rebuild graph failed:', error);
    throw error;
  }
};

/**
 * Regenerate analysis (summary, topics, mindMap, questions)
 * Clears error states and regenerates from document content
 * @param {string} notebookId - Notebook ID
 */
export const regenerateAnalysis = async notebookId => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/regenerate-analysis`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Regenerate analysis failed:', error);
    throw error;
  }
};

/**
 * Remove duplicate files from notebook
 * Keeps most recent version, removes older duplicates
 * @param {string} notebookId - Notebook ID
 */
export const removeDuplicates = async notebookId => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/remove-duplicates`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Remove duplicates failed:', error);
    throw error;
  }
};

/**
 * Clear analysis error states (removes error mindMap)
 * @param {string} notebookId - Notebook ID
 */
export const clearAnalysisErrors = async notebookId => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/clear-analysis-errors`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Clear analysis errors failed:', error);
    throw error;
  }
};

// ==========================================
// SRL-U (SEMANTIC ROLE LABELING) APIs
// ==========================================

/**
 * Extract SRL-U data from notebook documents
 * @param {string} notebookId - Notebook ID
 */
export const extractSRL = async notebookId => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/srl/extract`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] SRL extraction failed:', error);
    throw error;
  }
};

/**
 * Get Role View (natural language summary of SRL)
 * @param {string} notebookId - Notebook ID
 */
export const getRoleView = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/srl/role-view`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get role view failed:', error);
    throw error;
  }
};

/**
 * Generate AI scenarios from SRL data
 * @param {string} notebookId - Notebook ID
 */
export const generateScenarios = async notebookId => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/srl/scenarios`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Generate scenarios failed:', error);
    throw error;
  }
};

/**
 * Get SRL extraction statistics
 * @param {string} notebookId - Notebook ID
 */
export const getSRLStats = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/srl/stats`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get SRL stats failed:', error);
    throw error;
  }
};

// ==========================================
// AGI INTELLIGENCE ENGINE APIs
// ==========================================

/**
 * Run full AGI intelligence analysis
 * @param {string} notebookId - Notebook ID
 * @param {boolean} forceRefresh - Force re-analysis
 */
export const runIntelligenceAnalysis = async (notebookId, forceRefresh = false) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/analyze`,
      { forceRefresh }
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Intelligence analysis failed:', error);
    throw error;
  }
};

/**
 * Get domain-specific intelligence
 * @param {string} notebookId - Notebook ID
 * @param {string} domain - Domain (legal|fmcg|banking|universal)
 */
export const getDomainIntelligence = async (notebookId, domain) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/domain/${domain}`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get domain intelligence failed:', error);
    throw error;
  }
};

/**
 * Get action items from intelligence analysis
 * @param {string} notebookId - Notebook ID
 */
export const getIntelligenceActions = async notebookId => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/actions`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get actions failed:', error);
    throw error;
  }
};

/**
 * Get strategic scenarios
 * @param {string} notebookId - Notebook ID
 */
export const getIntelligenceScenarios = async notebookId => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/scenarios`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get scenarios failed:', error);
    throw error;
  }
};

/**
 * Get cross-domain insights
 * @param {string} notebookId - Notebook ID
 */
export const getCrossDomainInsights = async notebookId => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/cross-domain`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get cross-domain insights failed:', error);
    throw error;
  }
};

/**
 * Get impact chains (cause → effect → outcome)
 * @param {string} notebookId - Notebook ID
 */
export const getImpactChains = async notebookId => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/impact-chains`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get impact chains failed:', error);
    throw error;
  }
};

/**
 * Generate initial analysis / executive summary for a notebook
 * @param {string} notebookId - Notebook ID
 */
export const generateInitialAnalysis = async (notebookId, config) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/initial-analysis`,
      {},
      config
    );
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [IntelligenceStudio API] Generate initial analysis failed:', error);
    throw error;
  }
};

// ==========================================
// THREAD APIs
// ==========================================

/**
 * Get all threads for a notebook
 * @param {string} notebookId - Notebook ID
 */
export const getThreads = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/threads`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get threads failed:', error);
    throw error;
  }
};

/**
 * Create a new thread
 * @param {string} notebookId - Notebook ID
 * @param {Object} options - Thread options (title, partyView, scenarioMode)
 */
export const createThread = async (notebookId, options = {}) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/threads`, options);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Create thread failed:', error);
    throw error;
  }
};

/**
 * Get a specific thread
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID
 */
export const getThread = async (notebookId, threadId) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/threads/${threadId}`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get thread failed:', error);
    throw error;
  }
};

/**
 * Update a thread
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID
 * @param {Object} updates - Updates to apply
 */
export const updateThread = async (notebookId, threadId, updates) => {
  try {
    const response = await apiClient.patch(
      `${BASE_PATH}/notebooks/${notebookId}/threads/${threadId}`,
      updates
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Update thread failed:', error);
    throw error;
  }
};

/**
 * Delete a thread
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID
 */
export const deleteThread = async (notebookId, threadId) => {
  try {
    const response = await apiClient.delete(
      `${BASE_PATH}/notebooks/${notebookId}/threads/${threadId}`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Delete thread failed:', error);
    throw error;
  }
};

/**
 * Get thread messages
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID
 * @param {number} limit - Max messages to return
 */
export const getThreadMessages = async (notebookId, threadId, limit = 50, config) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/threads/${threadId}/messages?limit=${limit}`,
      config
    );
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [IntelligenceStudio API] Get messages failed:', error);
    throw error;
  }
};

/**
 * Add message to thread
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID
 * @param {Object} message - Message data
 */
export const addThreadMessage = async (notebookId, threadId, message, config) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/threads/${threadId}/messages`,
      message,
      config
    );
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [IntelligenceStudio API] Add message failed:', error);
    throw error;
  }
};

/**
 * Set thread party view
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID
 * @param {Object} party - Party info (partyId, partyName, role)
 */
export const setThreadParty = async (notebookId, threadId, party) => {
  try {
    const response = await apiClient.put(
      `${BASE_PATH}/notebooks/${notebookId}/threads/${threadId}/party`,
      party
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Set party failed:', error);
    throw error;
  }
};

// ==========================================
// SSE PROGRESS STREAMING
// ==========================================

/**
 * Create SSE connection for progress streaming
 * @param {string} notebookId - Notebook ID
 * @param {string} threadId - Thread ID (optional)
 * @param {Function} onEvent - Event handler
 * @param {string} authToken - Optional auth token for SSE
 * @returns {EventSource} - EventSource instance
 */
export const createProgressStream = (notebookId, threadId, onEvent, authToken = '') => {
  const resolvedToken = resolveSseToken(authToken);

  const traceSpan = createTraceSpan({
    apiName: 'sse.progress',
    pipelineStage: 'sse.progress',
    notebookId,
    threadId,
    tip: 'EventSource',
  });
  const traceQuery = getTraceQuery(traceSpan);
  const qs = new URLSearchParams({
    ...(threadId ? { threadId } : {}),
    ...(resolvedToken ? { token: resolvedToken } : {}),
    traceId: traceQuery.traceId,
    spanId: traceQuery.spanId,
  });
  const apiBase = resolveAbsoluteSseApiBaseUrl();
  const url = `${apiBase}/intelligence-studio/notebooks/${notebookId}/progress/stream?${qs.toString()}`;

  const eventSource = new EventSource(url, { withCredentials: true });
  let closed = false;

  const safeOnEvent = (type, payload) => {
    if (closed) return;
    try {
      const tracePayload = payload?.trace || payload?.context || null;
      if (tracePayload?.traceId && tracePayload?.spanId) {
        recordRemoteSpan({
          ...tracePayload,
          pipelineStage: tracePayload.pipelineStage || payload?.pipelineStage || type,
          status: type === 'error' ? 'error' : undefined,
        });
      } else if (payload?.traceId && payload?.spanId) {
        recordRemoteSpan({
          traceId: payload.traceId,
          spanId: payload.spanId,
          pipelineStage: payload?.pipelineStage || type,
          status: type === 'error' ? 'error' : undefined,
        });
      }
    } catch (_) {}
    onEvent(type, payload);
  };

  const parseAndEmit = type => event => {
    if (closed) return;
    try {
      const data = JSON.parse(event.data);
      safeOnEvent(type, data);
    } catch (err) {
      if (!closed) console.error('❌ [SSE] Parse error:', err);
    }
  };

  // Patch close() so we can prevent post-close events from hitting handlers.
  try {
    const originalClose = eventSource.close?.bind(eventSource);
    eventSource.close = () => {
      if (closed) return;
      closed = true;
      try {
        endTraceSpan(traceSpan.spanId, { status: 'ok', apiName: 'sse.progress' });
      } catch (_) {}
      try {
        originalClose?.();
      } catch (_) {}
    };
  } catch (_) {
    // no-op
  }

  eventSource.onmessage = parseAndEmit('message');

  eventSource.addEventListener('connected', parseAndEmit('connected'));

  eventSource.addEventListener('progress', parseAndEmit('progress'));

  // ✅ ENTERPRISE: Canonical notebook snapshot + delta updates (Phase-2 table + left column coherence)
  eventSource.addEventListener('notebook:snapshot', parseAndEmit('notebook:snapshot'));

  eventSource.addEventListener('notebook:update', parseAndEmit('notebook:update'));

  // ✅ Phase-2: per-file completion + comparison-ready signal
  eventSource.addEventListener('file:phase2_ready', parseAndEmit('file:phase2_ready'));

  // Optional fine-grained file updates
  eventSource.addEventListener('file:update', parseAndEmit('file:update'));

  // Per-file summary streaming (incremental engagement)
  eventSource.addEventListener('file:summary_final', parseAndEmit('file:summary_final'));

  eventSource.addEventListener('worker:start', parseAndEmit('worker:start'));

  eventSource.addEventListener('worker:complete', parseAndEmit('worker:complete'));

  eventSource.addEventListener('worker:error', parseAndEmit('worker:error'));

  eventSource.addEventListener('phase:start', parseAndEmit('phase:start'));

  eventSource.addEventListener('phase:complete', parseAndEmit('phase:complete'));

  eventSource.addEventListener('summary:mid', parseAndEmit('summary:mid'));

  eventSource.addEventListener('summary:final', parseAndEmit('summary:final'));

  eventSource.addEventListener('ping', () => {
    // Keep-alive, ignore
  });

  eventSource.onerror = error => {
    if (closed) return;
    console.error('❌ [SSE] Connection error:', error);
    try {
      endTraceSpan(traceSpan.spanId, {
        status: 'error',
        apiName: 'sse.progress',
        error: error?.message || 'SSE connection error',
      });
    } catch (_) {}
    safeOnEvent('error', { error: 'Connection lost' });
  };

  return eventSource;
};

/**
 * Get raw vector context (for debugging)
 * @param {string} notebookId - Notebook ID
 * @param {string} query - Search query
 * @param {number} limit - Result limit
 */
export const getContext = async (notebookId, query, limit = 20) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/context`, {
      query,
      limit,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get context failed:', error);
    throw error;
  }
};

/**
 * Compare multiple files
 * @param {string} notebookId - Notebook ID
 * @param {string[]} fileIds - File IDs to compare
 */
export const compareFiles = async (notebookId, fileIds) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/compare`, {
      fileIds,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Compare files failed:', error);
    throw error;
  }
};

/**
 * Create a manual relationship between entities
 * @param {string} entityA - Source entity ID
 * @param {string} entityB - Target entity ID
 * @param {string} type - Relationship type
 * @param {string} notebookId - Notebook ID
 */
export const createRelationship = async (entityA, entityB, type, notebookId) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/relationships`, {
      a: entityA,
      b: entityB,
      type,
      notebookId,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Create relationship failed:', error);
    throw error;
  }
};

/**
 * Get entities in a notebook for relationship creation
 * @param {string} notebookId - Notebook ID
 */
export const getEntities = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/entities`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get entities failed:', error);
    throw error;
  }
};

/**
 * Get file content (extracted text) for preview
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 */
export const getFileContent = async (notebookId, fileId) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/files/${fileId}/content`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get file content failed:', error);
    throw error;
  }
};

/**
 * Get notebook with file contents included
 * @param {string} notebookId - Notebook ID
 */
export const getNotebookWithContent = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/full`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get notebook with content failed:', error);
    throw error;
  }
};

// ==========================================
// TIMELINE APIs
// ==========================================

/**
 * Build timeline from vault documents
 * @param {string} notebookId - Notebook ID
 * @param {string[]} fileIds - Optional file IDs
 */
export const buildTimelineFromDocs = async (notebookId, fileIds = null) => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/timeline`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Build timeline failed:', error);
    throw error;
  }
};

/**
 * Get existing timeline from graph
 * @param {string} notebookId - Notebook ID
 */
export const getTimelineData = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/timeline`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get timeline failed:', error);
    throw error;
  }
};

// ==========================================
// CLAUSE MAPPING APIs
// ==========================================

/**
 * Map clauses across files (Harvey-style)
 * @param {string} notebookId - Notebook ID
 * @param {string[]} fileIds - File IDs to compare
 */
export const mapClausesAcrossFiles = async (notebookId, fileIds) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/clauses`, {
      fileIds,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Map clauses failed:', error);
    throw error;
  }
};

// ==========================================
// FULL ANALYSIS API
// ==========================================

/**
 * Full vault analysis (summary + timeline + clause mapping)
 * @param {string} notebookId - Notebook ID
 * @param {string[]} fileIds - Optional file IDs
 */
export const analyzeFullVault = async (notebookId, fileIds = null) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/analyze`, {
      fileIds,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Full analysis failed:', error);
    throw error;
  }
};

// ==========================================
// ADVANCED INTELLIGENCE APIs
// ==========================================

/**
 * Generate smart summary for notebook
 * @param {string} notebookId - Notebook ID
 */
export const getSmartSummary = async notebookId => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/summary`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get summary failed:', error);
    throw error;
  }
};

/**
 * Run opponent simulation
 * @param {string} notebookId - Notebook ID
 * @param {Object} options - Simulation options
 */
export const runOpponentSim = async (notebookId, options = {}) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/opponent-sim`,
      options
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Opponent sim failed:', error);
    throw error;
  }
};

/**
 * Build narrative from documents
 * @param {string} notebookId - Notebook ID
 */
export const buildNarrative = async notebookId => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/narrative`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Build narrative failed:', error);
    throw error;
  }
};

/**
 * Generate risk map
 * @param {string} notebookId - Notebook ID
 */
export const getRiskMap = async notebookId => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/intelligence/risk-map`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get risk map failed:', error);
    throw error;
  }
};

/**
 * Get Excel intelligence for a file
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 */
export const getExcelIntelligence = async (notebookId, fileId) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/files/${fileId}/excel-intelligence`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get Excel intelligence failed:', error);
    throw error;
  }
};

/**
 * Get processing status for a file
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 */
export const getFileProcessingStatus = async (notebookId, fileId) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/files/${fileId}/status`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get file status failed:', error);
    throw error;
  }
};

/**
 * Get OCR details for a file
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID
 */
export const getOCRDetails = async (notebookId, fileId) => {
  try {
    const response = await apiClient.get(
      `${BASE_PATH}/notebooks/${notebookId}/files/${fileId}/ocr`
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get OCR details failed:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time file processing updates
 * @param {string} notebookId - Notebook ID
 * @param {Function} onUpdate - Update callback
 */
export const subscribeToProcessingUpdates = (notebookId, onUpdate) => {
  const baseUrl = resolveAbsoluteSseApiBaseUrl().replace(/\/api\/?$/, '');
  const traceSpan = createTraceSpan({
    apiName: 'sse.processing',
    pipelineStage: 'sse.processing',
    notebookId,
    tip: 'EventSource',
  });
  const traceQuery = getTraceQuery(traceSpan);
  const qs = new URLSearchParams({
    traceId: traceQuery.traceId,
    spanId: traceQuery.spanId,
  });
  const eventSource = new EventSource(
    `${baseUrl}${BASE_PATH}/notebooks/${notebookId}/processing/stream?${qs.toString()}`
  );

  eventSource.onmessage = event => {
    try {
      const data = JSON.parse(event.data);
      try {
        if (data?.trace?.traceId && data?.trace?.spanId) {
          recordRemoteSpan(data.trace);
        }
      } catch (_) {}
      onUpdate(data);
    } catch (e) {
      console.error('Failed to parse processing update:', e);
    }
  };

  // Processing-specific events
  const events = [
    'file:queued',
    'file:processing',
    'file:done',
    'file:error',
    'ocr:start',
    'ocr:batch_progress',
    'ocr:complete',
    'excel:parsing_sheet',
    'excel:intel_done',
    'chunking:progress',
    'embedding:progress',
    'graph:progress',
  ];

  events.forEach(eventName => {
    eventSource.addEventListener(eventName, event => {
      try {
        const data = JSON.parse(event.data);
        try {
          if (data?.trace?.traceId && data?.trace?.spanId) {
            recordRemoteSpan(data.trace);
          }
        } catch (_) {}
        onUpdate({ type: eventName, ...data });
      } catch (e) {
        console.error(`Failed to parse ${eventName}:`, e);
      }
    });
  });

  eventSource.onerror = () => {
    try {
      endTraceSpan(traceSpan.spanId, { status: 'error', error: 'SSE processing error' });
    } catch (_) {}
    eventSource.close();
  };

  return () => eventSource.close();
};

// ==========================================
// WAVE 12 - MULTI-AGENT INTELLIGENCE APIs
// ==========================================

/**
 * Query using NotebookBrain V2 (multi-agent reasoning)
 * @param {string} notebookId - Notebook ID
 * @param {string} question - User question
 * @param {Object} options - Query options (mode, enableArbitration)
 */
export const queryBrainV2 = async (notebookId, question, options = {}) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/brain-v2/query`, {
      question,
      ...options,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Brain V2 query failed:', error);
    throw error;
  }
};

/**
 * Extract clauses using V3 engine
 * @param {string} notebookId - Notebook ID
 * @param {string} fileId - File ID (optional)
 */
export const extractClausesV3 = async (notebookId, fileId = null) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/clauses/extract`, {
      fileId,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Clause extraction failed:', error);
    throw error;
  }
};

/**
 * Compare two contracts using V2 diff engine
 * @param {string} notebookId - Notebook ID
 * @param {string} fileIdA - First file ID
 * @param {string} fileIdB - Second file ID
 */
export const compareContractsV2 = async (notebookId, fileIdA, fileIdB) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/contracts/compare`,
      {
        fileIdA,
        fileIdB,
      }
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Contract comparison failed:', error);
    throw error;
  }
};

/**
 * Generate redline markup between two contracts
 * @param {string} notebookId - Notebook ID
 * @param {string} fileIdA - Original file ID
 * @param {string} fileIdB - Modified file ID
 */
export const generateRedline = async (notebookId, fileIdA, fileIdB) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/contracts/redline`,
      {
        fileIdA,
        fileIdB,
      }
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Redline generation failed:', error);
    throw error;
  }
};

/**
 * Compare across different notebooks
 * @param {string} notebookIdA - First notebook ID
 * @param {string} notebookIdB - Second notebook ID
 * @param {string} question - Comparison question
 */
export const compareAcrossNotebooks = async (notebookIdA, notebookIdB, question) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/cross-compare`, {
      notebookIdA,
      notebookIdB,
      question,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Cross-notebook comparison failed:', error);
    throw error;
  }
};

// ==========================================
// WAVE 13 - EXPLAINABILITY & COMPLIANCE APIs
// ==========================================

/**
 * Get explanation for how an answer was generated
 * @param {string} notebookId - Notebook ID
 * @param {string} queryId - Query ID to explain
 */
export const explainAnswer = async (notebookId, queryId) => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/explain/${queryId}`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get explanation failed:', error);
    throw error;
  }
};

/**
 * Get answer with citations only (zero-hallucination mode)
 * @param {string} notebookId - Notebook ID
 * @param {string} question - Question
 * @param {Object} options - Options (strictMode)
 */
export const answerWithCitations = async (notebookId, question, options = {}) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/answer-cited`, {
      question,
      strictMode: options.strictMode ?? true,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Cited answer failed:', error);
    throw error;
  }
};

/**
 * Map sentences in an answer to their sources
 * @param {string} notebookId - Notebook ID
 * @param {string} answer - Answer text to map
 */
export const mapSentencesToSources = async (notebookId, answer) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/map-sources`, {
      answer,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Source mapping failed:', error);
    throw error;
  }
};

/**
 * Evaluate quality of an answer
 * @param {string} notebookId - Notebook ID
 * @param {string} question - Original question
 * @param {string} answer - Answer to evaluate
 */
export const evaluateAnswer = async (notebookId, question, answer) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/evaluate-answer`, {
      question,
      answer,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Answer evaluation failed:', error);
    throw error;
  }
};

/**
 * Get audit logs for a notebook
 * @param {string} notebookId - Notebook ID
 * @param {Object} filters - Filters (startDate, endDate, eventType)
 */
export const getAuditLogs = async (notebookId, filters = {}) => {
  try {
    const params = new URLSearchParams(filters).toString();
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/audit?${params}`);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get audit logs failed:', error);
    throw error;
  }
};

/**
 * Generate compliance report
 * @param {string} notebookId - Notebook ID
 * @param {Object} params - Report parameters (startDate, endDate)
 */
export const generateComplianceReport = async (notebookId, params = {}) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/compliance-report`,
      params
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Compliance report failed:', error);
    throw error;
  }
};

/**
 * Export audit trail
 * @param {string} notebookId - Notebook ID
 * @param {Object} params - Export parameters (format, startDate, endDate)
 */
export const exportAuditTrail = async (notebookId, params = {}) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/audit/export`,
      params
    );
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Audit export failed:', error);
    throw error;
  }
};

// ==========================================
// 📊 ENTERPRISE: FEEDBACK SYSTEM
// ==========================================

/**
 * Send feedback for a message (thumbs up/down)
 * @param {string} notebookId - Notebook ID
 * @param {Object} feedback - Feedback data
 */
export const sendFeedback = async (notebookId, feedback) => {
  try {
    const response = await apiClient.post(
      `${BASE_PATH}/notebooks/${notebookId}/feedback`,
      feedback
    );
    return response;
  } catch (error) {
    console.warn('⚠️ [IntelligenceStudio API] Feedback send failed (non-critical):', error.message);
    // Non-critical - don't throw
    return { success: false, error: error.message };
  }
};

/**
 * Get feedback statistics for a notebook
 * @param {string} notebookId - Notebook ID
 */
export const getFeedbackStats = async notebookId => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/notebooks/${notebookId}/feedback/stats`);
    return response;
  } catch (error) {
    console.warn('⚠️ [IntelligenceStudio API] Feedback stats failed:', error.message);
    return { success: false, stats: {} };
  }
};

// ==========================================
// ✅ ENTERPRISE: NOTEBOOK FINALIZATION APIs
// ==========================================

/**
 * Finalize a stuck notebook (force ready state)
 * @param {string} notebookId - Notebook ID
 * @param {Object} options - Options (force, skipVerification)
 */
export const finalizeNotebook = async (notebookId, options = {}) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/${notebookId}/finalize`, options);
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Finalize notebook failed:', error);
    throw error;
  }
};

/**
 * Run watchdog check for stuck notebooks
 * @param {number} maxAgeMinutes - Max age in minutes before marking as stuck
 */
export const runWatchdog = async (maxAgeMinutes = 30) => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/notebooks/watchdog`, { maxAgeMinutes });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Run watchdog failed:', error);
    throw error;
  }
};

// ==========================================
// POST-INGESTION ANALYSIS ENGINE APIs
// ==========================================

const ANALYSIS_PATH = '/api/analysis';

/**
 * Get analysis status for a vault
 * @param {string} notebookId - Vault/Notebook ID
 */
export const getAnalysisStatus = async notebookId => {
  try {
    const response = await apiClient.get(`${ANALYSIS_PATH}/vault/${notebookId}/status`);
    return response;
  } catch (error) {
    console.error('❌ [Analysis API] Get status failed:', error);
    return { success: false, error: error?.message || 'Failed to get analysis status' };
  }
};

/**
 * Validate file IDs against analysis vault (canonical notebook store)
 * @param {string} notebookId - Vault/Notebook ID
 * @param {string[]} fileIds - File IDs to validate
 */
export const validateAnalysisFiles = async (notebookId, fileIds = []) => {
  try {
    const response = await apiClient.post(`${ANALYSIS_PATH}/vault/${notebookId}/validate`, {
      fileIds,
    });
    return response;
  } catch (error) {
    console.error('❌ [Analysis API] Validate files failed:', error);
    return { success: false, error: error?.message || 'Failed to validate files' };
  }
};

/**
 * Get all analysis artifacts for a vault
 * @param {string} notebookId - Vault/Notebook ID
 */
export const getVaultAnalysis = async (notebookId, config) => {
  try {
    const response = await apiClient.get(`${ANALYSIS_PATH}/vault/${notebookId}`, config);
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [Analysis API] Get vault analysis failed:', error);
    return { success: false, error: error?.message || 'Failed to get vault analysis' };
  }
};

/**
 * Get analysis for a specific file
 * @param {string} notebookId - Vault/Notebook ID
 * @param {string} fileId - File ID
 */
export const getFileAnalysis = async (notebookId, fileId) => {
  try {
    const response = await apiClient.get(`${ANALYSIS_PATH}/vault/${notebookId}/file/${fileId}`);
    return response;
  } catch (error) {
    console.error('❌ [Analysis API] Get file analysis failed:', error);
    return { success: false, error: error?.message || 'Failed to get file analysis' };
  }
};

/**
 * Get cross-file analysis for a vault
 * @param {string} notebookId - Vault/Notebook ID
 */
export const getCrossFileAnalysis = async notebookId => {
  try {
    const response = await apiClient.get(`${ANALYSIS_PATH}/vault/${notebookId}/cross-file`);
    return response;
  } catch (error) {
    console.error('❌ [Analysis API] Get cross-file analysis failed:', error);
    return { success: false, error: error?.message || 'Failed to get cross-file analysis' };
  }
};

/**
 * Run post-ingestion analysis for a vault
 * @param {string} notebookId - Vault/Notebook ID
 * @param {Object} options - { includeCrossFile, force }
 */
export const runAnalysis = async (notebookId, options = {}, config) => {
  try {
    const response = await apiClient.post(`${ANALYSIS_PATH}/vault/${notebookId}/run`, options, config);
    return response;
  } catch (error) {
    if (isCanceledRequest(error)) throw error;
    console.error('❌ [Analysis API] Run analysis failed:', error);
    throw error;
  }
};

/**
 * Run analysis for a single file
 * @param {string} notebookId - Vault/Notebook ID
 * @param {string} fileId - File ID
 * @param {Object} options - { force }
 */
export const runFileAnalysis = async (notebookId, fileId, options = {}) => {
  try {
    const response = await apiClient.post(
      `${ANALYSIS_PATH}/vault/${notebookId}/file/${fileId}/run`,
      options
    );
    return response;
  } catch (error) {
    console.error('❌ [Analysis API] Run file analysis failed:', error);
    throw error;
  }
};

/**
 * Run cross-file analysis for a vault
 * @param {string} notebookId - Vault/Notebook ID
 */
export const runCrossFileAnalysis = async notebookId => {
  try {
    const response = await apiClient.post(`${ANALYSIS_PATH}/vault/${notebookId}/cross-file`);
    return response;
  } catch (error) {
    console.error('❌ [Analysis API] Run cross-file analysis failed:', error);
    throw error;
  }
};

/**
 * Dev-only: list incident logs (Phase 8)
 * @param {Object} filters
 */
export const getIncidentLogs = async (filters = {}) => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/debug/incidents`, {
      params: filters,
    });
    return response;
  } catch (error) {
    console.error('❌ [IntelligenceStudio API] Get incident logs failed:', error);
    throw error;
  }
};

export default {
  getStatus,
  getQuotaStatus,
  createNotebook,
  listNotebooks,
  getNotebook,
  getNotebookState,
  updateNotebook,
  deleteNotebook,
  checkFileBeforeUpload, // ✅ Smart pre-check
  smartUpload, // ✅ Smart upload with pre-check
  uploadFile, // Direct upload (no pre-check)
  // ✅ PHASE 2/7: Semantic memory APIs (required by Intelligence Studio settings UI)
  getMemorySettings,
  updateMemorySettings,
  queryMemory,
  clearMemory,
  subscribeProgress,
  deleteFile,
  renameFile,
  reprocessFile, // ✅ Force re-process poor quality files
  askQuestion,
  getChatHistory,
  clearChatHistory,
  // Brain APIs
  queryBrain,
  compareDocuments,
  getGraph,
  findConflicts,
  deterministicCount,
  buildTimeline,
  // Insight Memory + Vault Identity
  getInsightMemory,
  upsertInsightMemory,
  recordInsightMemoryEvent,
  generateVaultNarrative,
  askGraph,
  hybridSearch,
  // Query Engine APIs
  runQuery,
  getKnowledgeGraph,
  rebuildGraph,
  regenerateAnalysis,
  clearAnalysisErrors,
  getContext,
  // SRL-U APIs
  extractSRL,
  getRoleView,
  generateScenarios,
  getSRLStats,
  // AGI Intelligence APIs
  runIntelligenceAnalysis,
  getDomainIntelligence,
  getIntelligenceActions,
  getIntelligenceScenarios,
  getCrossDomainInsights,
  getImpactChains,
  // Initial Analysis
  generateInitialAnalysis,
  // Thread APIs
  getThreads,
  createThread,
  getThread,
  updateThread,
  deleteThread,
  getThreadMessages,
  addThreadMessage,
  setThreadParty,
  // SSE Progress Stream
  createProgressStream,
  compareFiles,
  createRelationship,
  getEntities,
  // Timeline APIs
  buildTimelineFromDocs,
  getTimelineData,
  // Clause Mapping APIs
  mapClausesAcrossFiles,
  // Full Analysis
  analyzeFullVault,
  // File Content APIs
  getFileContent,
  getNotebookWithContent,
  // Advanced Intelligence APIs
  getSmartSummary,
  runOpponentSim,
  buildNarrative,
  getRiskMap,
  getExcelIntelligence,
  getFileProcessingStatus,
  getOCRDetails,
  subscribeToProcessingUpdates,
  // Wave 12 - Multi-Agent Intelligence
  queryBrainV2,
  extractClausesV3,
  compareContractsV2,
  generateRedline,
  compareAcrossNotebooks,
  // Wave 13 - Explainability & Compliance
  explainAnswer,
  answerWithCitations,
  mapSentencesToSources,
  evaluateAnswer,
  getAuditLogs,
  generateComplianceReport,
  exportAuditTrail,
  // Enterprise - Feedback System
  sendFeedback,
  getFeedbackStats,
  // Enterprise - Notebook Finalization
  finalizeNotebook,
  runWatchdog,
  // Post-Ingestion Analysis Engine
  getAnalysisStatus,
  validateAnalysisFiles,
  getVaultAnalysis,
  getFileAnalysis,
  getCrossFileAnalysis,
  runAnalysis,
  runFileAnalysis,
  runCrossFileAnalysis,
  getIncidentLogs,
};
